import React from 'react';
import { Box, Link, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { useSalonPublicTheme } from '../../../../context/SalonPublicThemeContext';
import SalonTemplateAbout from '../blocks/SalonTemplateAbout';
import SalonTemplateBookingAction from '../blocks/SalonTemplateBookingAction';
import SalonTemplateContactLocation from '../blocks/SalonTemplateContactLocation';
import SalonTemplateGallery from '../blocks/SalonTemplateGallery';
import SalonTemplateOpeningHours from '../blocks/SalonTemplateOpeningHours';
import SalonTemplateReviews from '../blocks/SalonTemplateReviews';
import SalonTemplateServices from '../blocks/SalonTemplateServices';
import SalonTemplateTeam from '../blocks/SalonTemplateTeam';
import PureStudioIdentity from './PureStudioIdentity';


const PureStudioTemplate = ({
  viewModel,
  actions,
  mode = 'public',
  viewport = 'desktop',
  direction = 'ltr',
}) => {
  const { t } = useTranslation();
  const { layout, tokens: theme } = useSalonPublicTheme();
  const identity = viewModel?.identity || {};
  const photos = Array.isArray(viewModel?.photos) ? viewModel.photos : [];
  const reviews = Array.isArray(viewModel?.reviews) ? viewModel.reviews : [];
  const openingHours = Array.isArray(viewModel?.openingHours) ? viewModel.openingHours : [];
  const closures = Array.isArray(viewModel?.closures) ? viewModel.closures : [];
  const categories = Array.isArray(viewModel?.serviceCategories)
    ? viewModel.serviceCategories.filter((category) => (
      Array.isArray(category?.services) && category.services.length > 0
    ))
    : [];
  const navigationCategories = categories.filter((category) => (
    typeof category?.name === 'string' && category.name.trim()
  ));
  const publicState = viewModel?.publicState || {};
  const visibility = layout.tokens.visibility;
  const mobile = viewport === 'mobile';
  const hasPracticalInfo = Boolean(
    visibility.opening_hours && (openingHours.length > 0 || closures.length > 0),
  );
  const hasBookableService = Boolean(
    viewModel?.capabilities?.canClientBook
    && categories.some((category) => category.services.some((item) => item?.canBook && item?.raw)),
  );

  return (
    <Box
      data-testid="salon-template-renderer"
      data-salon-template="pure_studio"
      data-mode={mode}
      data-viewport={viewport}
      dir={direction}
      sx={{
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        minHeight: 0,
        minWidth: 0,
        width: '100%',
        backgroundColor: theme.salon.background,
      }}
    >
      {hasPracticalInfo ? (
        <Box
          data-testid="pure-studio-practical-info"
          sx={{
            width: '100%',
            px: { xs: 2, md: 4 },
            pt: 2,
            boxSizing: 'border-box',
          }}
        >
          <SalonTemplateOpeningHours
            data={{ hours: openingHours, closures }}
            variant="week_strip"
            visible={visibility.opening_hours}
            markClosuresAsBlock={false}
          />
        </Box>
      ) : null}

      <PureStudioIdentity
        data={{
          ...identity,
          heroPhoto: photos[0]?.src,
          averageRating: viewModel?.averageRating,
          reviewCount: reviews.length,
          isFavorite: viewModel?.capabilities?.isFavorite,
        }}
        mode={mode}
        onBack={actions?.onBack}
        onToggleFavorite={actions?.onToggleFavorite}
        onOpenMobileInfo={actions?.onOpenMobileInfo}
        onBook={actions?.onOpenBooking}
        onContact={actions?.onContact}
        clientChatEnabled={identity.raw?.client_chat_enabled}
      />

      <Box
        data-testid="pure-studio-scroll-region"
        data-scroll-container="document"
        sx={{
          flex: 'none',
          minHeight: 'auto',
          width: '100%',
          overflowY: 'visible',
          overflowX: 'hidden',
        }}
      >
        <Box
          component="main"
          data-testid="pure-studio-content"
          data-layout={mobile ? 'single-column' : 'efficient-catalog'}
          sx={{
            width: '100%',
            maxWidth: 1180,
            mx: 'auto',
            px: { xs: 2, md: 5 },
            py: { xs: 3, md: 5 },
            pb: { xs: 5, md: 7 },
            boxSizing: 'border-box',
          }}
        >
          {navigationCategories.length > 0 ? (
            <Box
              component="nav"
              aria-label={t('landing_salon_details.our_services')}
              data-salon-template-block="categories"
              sx={{ mb: 3 }}
            >
              <Stack direction="row" spacing={1.5} useFlexGap flexWrap="wrap">
                {navigationCategories.map((category) => (
                  <Link
                    key={category.id}
                    href={`#pure-studio-category-${category.id}`}
                    underline="none"
                    sx={{
                      px: 2,
                      py: 0.75,
                      color: theme.salon.secondary,
                      fontWeight: 700,
                      border: `1px solid ${theme.salon.border}`,
                      borderRadius: 99,
                      backgroundColor: theme.salon.surface,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        color: theme.salon.primary,
                        borderColor: theme.salon.primary,
                        backgroundColor: theme.colors.background.primary,
                      },
                    }}
                  >
                    {category.name}
                  </Link>
                ))}
              </Stack>
            </Box>
          ) : null}

          <SalonTemplateServices
            data={categories}
            variant="price_table"
            mode={mode}
            viewport={viewport}
            loading={Boolean(publicState.servicesLoading)}
            onBookService={actions?.onBookService}
            categoryAnchorPrefix="pure-studio-category"
          />

          {hasBookableService ? (
            <Box
              data-testid="pure-studio-booking-flow"
              sx={{
                position: 'static',
                width: '100%',
                mt: 4,
                p: { xs: 2, md: 3 },
                boxSizing: 'border-box',
                borderRadius: 3,
                border: `1px solid ${theme.salon.border}`,
                backgroundColor: theme.salon.surfaceMuted,
              }}
            >
              <SalonTemplateBookingAction
                data={{ available: true, targetId: 'services' }}
                variant="inline"
                lightSurface
                mode={mode}
                onBook={actions?.onOpenBooking}
              />
            </Box>
          ) : null}

          <SalonTemplateReviews
            data={{
              items: reviews,
              averageRating: viewModel?.averageRating,
              isAuthenticated: Boolean(publicState.isAuthenticated),
              isLoading: Boolean(publicState.reviewsLoading),
              error: publicState.reviewsError || null,
            }}
            variant="compact"
            visible={visibility.reviews}
            mode={mode}
            onOpenReviewComposer={actions?.onOpenReviewComposer}
            onOpenAllReviews={actions?.onOpenAllReviews}
          />
          <SalonTemplateAbout
            data={identity}
            variant="compact"
            visible={Boolean(identity.description)}
          />
          <SalonTemplateGallery
            data={photos}
            salonId={identity.id}
            variant="strip"
            visible={visibility.gallery}
            mode={mode}
            onSelectPhoto={actions?.onSelectPhoto}
            onNavigatePhoto={actions?.onNavigatePhoto}
            selectedIndex={publicState.photoIndex}
          />
          <SalonTemplateTeam
            data={viewModel?.team}
            variant="cards"
            visible={visibility.team}
            mode={mode}
            onSelectMember={actions?.onSelectTeamMember}
          />
          <SalonTemplateContactLocation
            data={identity}
            variant="compact"
            visible={visibility.location}
            mode={mode}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default PureStudioTemplate;
